import { NavLink } from 'react-router-dom';
import {
  Building2,
  Users,
  KanbanSquare,
  CalendarDays,
  Euro,
  Receipt,
  Settings,
  Wrench,
  HelpCircle,
  Gauge,
  LayoutGrid,
  UserCheck,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useMe } from '../api/me';

const nav: { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/viviendas', label: 'Viviendas', icon: Building2 },
  { to: '/clientes', label: 'Clientes', icon: Users },
  // Los avisos que abren los inquilinos desde su portal.
  { to: '/incidencias', label: 'Incidencias', icon: Wrench },
  { to: '/operaciones', label: 'Operaciones', icon: KanbanSquare },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays },
  { to: '/comisiones', label: 'Comisiones', icon: Euro },
  { to: '/contabilidad', label: 'Contabilidad', icon: Receipt },
];

// Lo que cuesta la plataforma y sus facturas: solo para el admin (Florent).
const costes = { to: '/costes', label: 'Costes', icon: Gauge };
// Lo que enseña la web de cada servicio: solo el admin cambia la web.
const servicios = { to: '/servicios', label: 'Servicios', icon: LayoutGrid };
// Las cuentas que la gente crea en la web: solo el admin.
const cuentas = { to: '/cuentas', label: 'Cuentas', icon: UserCheck };
// Cómo va la web: visitas, de dónde llegan y qué traen. Solo el admin.
const tuWeb = { to: '/tu-web', label: 'Tu web', icon: TrendingUp };

export default function Sidebar() {
  const { data: me } = useMe();
  const items = me?.groups.includes('admin') ? [...nav, costes, servicios, cuentas, tuWeb] : nav;

  return (
    <>
      {/* Desktop: lateral fijo */}
      <aside className="hidden md:flex shrink-0 w-52 border-r border-deep/10 bg-surface min-h-[calc(100vh-48px)] flex-col justify-between py-4">
        <nav className="flex flex-col gap-1 px-3">
          {items.map((item) => (
            <Item key={item.to} {...item} />
          ))}
        </nav>
        <nav className="flex flex-col gap-1 px-3">
          {/* La guía se auto-hospeda en el CRM (public/guia.html): es del cliente
              y no depende de un enlace externo. Se abre en pestaña nueva, por eso
              es un <a> normal y no un NavLink del router. */}
          <a
            href="/guia.html"
            target="_blank"
            rel="noopener"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-ink/60 transition-colors hover:bg-soft hover:text-deep"
          >
            <HelpCircle size={18} strokeWidth={1.75} className="shrink-0" />
            Ayuda
          </a>
          <Item to="/ajustes" label="Ajustes" icon={Settings} />
        </nav>
      </aside>

      {/* Móvil: barra horizontal deslizable bajo el TopBar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 flex overflow-x-auto border-t border-deep/10 bg-surface px-2 py-1.5 gap-1 [scrollbar-width:none]">
        {[...items, { to: '/ajustes', label: 'Ajustes', icon: Settings }].map((item) => (
          <MobileItem key={item.to} {...item} />
        ))}
        <a
          href="/guia.html"
          target="_blank"
          rel="noopener"
          className="flex min-w-[64px] flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-[0.62rem] font-semibold text-ink/50"
        >
          <HelpCircle size={18} strokeWidth={1.75} />
          Ayuda
        </a>
      </nav>
    </>
  );
}

function Item({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors',
          isActive
            ? 'bg-soft text-deep font-bold'
            : 'text-ink/60 hover:bg-soft hover:text-deep',
        )
      }
    >
      <Icon size={18} strokeWidth={1.75} className="shrink-0" />
      {label}
    </NavLink>
  );
}

function MobileItem({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'flex min-w-[64px] flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-[0.62rem] font-semibold transition-colors',
          isActive ? 'text-brand' : 'text-ink/50',
        )
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}
